import React, { useState, useEffect } from 'react';
import { Upload, Button, message as antdMessage, Alert, Progress, Typography } from 'antd';
import { UploadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { UploadProps, RcFile, UploadFile as AntUploadFile } from 'antd/es/upload/interface';
import { uploadBom, MrpData, BomUploadApiResponse } from '../../services/api';

const { Text } = Typography;

interface BomUploadProps {
  mrpData: MrpData | null;
  onBomProcessed: (data: BomUploadApiResponse) => void;
  disabled: boolean;
}

const BomUpload: React.FC<BomUploadProps> = ({ mrpData, onBomProcessed, disabled }) => {
  const [fileList, setFileList] = useState<AntUploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [estimatedTime, setEstimatedTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Timer for elapsed time
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      const startTime = Date.now();
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getFileSize = (file: AntUploadFile): number => {
    return file.size || 0;
  };

  const estimateProcessingTime = (fileSize: number, mrpEntries: number): number => {
    // More complex estimation for BOM processing
    const sizeInMB = fileSize / (1024 * 1024);
    const baseMrpTime = Math.ceil(mrpEntries / 1000) * 2; // 2 seconds per 1000 MRP entries
    const baseFileTime = Math.max(10, Math.ceil(sizeInMB * 15)); // 15 seconds per MB, minimum 10
    return baseMrpTime + baseFileTime;
  };

  const simulateProgress = (duration: number) => {
    const steps = [
      { step: 'Reading BOM Excel file...', progress: 8, delay: 800 },
      { step: 'Reading MRP JSON data...', progress: 15, delay: 400 },
      { step: 'Validating columns...', progress: 25, delay: 600 },
      { step: 'Calculating depth levels...', progress: 40, delay: 1200 },
      { step: 'Classifying manufacturing types...', progress: 55, delay: 1000 },
      { step: 'Mapping production times...', progress: 75, delay: 1500 },
      { step: 'Validating root nodes...', progress: 85, delay: 600 },
      { step: 'Preparing final dataset...', progress: 95, delay: 400 },
      { step: 'Serializing data...', progress: 100, delay: 300 }
    ];

    let currentStepIndex = 0;
    const totalSteps = steps.length;
    const stepDuration = duration / totalSteps;

    const processStep = () => {
      if (currentStepIndex < steps.length && loading) {
        const { step, progress } = steps[currentStepIndex];
        setCurrentStep(step);
        setProgress(progress);
        currentStepIndex++;
        
        if (currentStepIndex < steps.length) {
          setTimeout(processStep, stepDuration * 1000);
        }
      }
    };

    processStep();
  };

  const props: UploadProps = {
    onRemove: (file) => {
      setFileList([]);
      setError(null);
      setSuccessMessage(null);
      setEstimatedTime(null);
    },
    beforeUpload: (file: RcFile) => {
      if (!file.name.match(/\.(xlsx|xls)$/)) {
        antdMessage.error(`${file.name} is not an Excel file (.xls or .xlsx)`);
        return Upload.LIST_IGNORE;
      }
      
      const fileSize = file.size;
      const mrpEntries = mrpData ? 
        Object.keys(mrpData.eigen_dict).length + Object.keys(mrpData.plan_dict).length : 0;
      const estimated = estimateProcessingTime(fileSize, mrpEntries);
      setEstimatedTime(estimated);

      const antFile: AntUploadFile = {
        uid: file.uid,
        name: file.name,
        status: 'done',
        originFileObj: file,
        size: file.size,
        type: file.type,
      };
      
      setFileList([antFile]);
      setError(null);
      setSuccessMessage(null);
      
      console.log(`BOM file selected: ${file.name} (${(fileSize / 1024 / 1024).toFixed(2)} MB)`);
      console.log(`MRP entries available: ${mrpEntries}`);
      console.log(`Estimated processing time: ${estimated} seconds`);
      
      return false;
    },
    fileList,
    maxCount: 1,
    disabled: disabled,
  };

  const handleUpload = async () => {
    if (fileList.length === 0 || !fileList[0]) {
      antdMessage.error('Please select a BOM Excel file first.');
      return;
    }

    const bomExcelFileFromState = fileList[0].originFileObj as File | undefined;

    if (!bomExcelFileFromState) {
        antdMessage.error('Could not retrieve the BOM Excel file. Please try selecting it again.');
        setError('Internal error: File object not found.');
        return;
    }

    if (!mrpData) {
      antdMessage.error('MRP data is not available. Please process MRP file and select a Werk first.');
      return;
    }

    const fileSize = getFileSize(fileList[0]);
    const mrpEntries = Object.keys(mrpData.eigen_dict).length + Object.keys(mrpData.plan_dict).length;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setProgress(0);
    setCurrentStep('Preparing upload...');
    setElapsedTime(0);

    console.log('Starting BOM file upload and processing...');
    console.log(`File: ${bomExcelFileFromState.name}, Size: ${(fileSize / 1024 / 1024).toFixed(2)} MB`);
    console.log(`MRP data: ${Object.keys(mrpData.eigen_dict).length} eigen, ${Object.keys(mrpData.plan_dict).length} plan entries`);

    // Start progress simulation
    if (estimatedTime) {
      simulateProgress(estimatedTime);
    }

    try {
      const response = await uploadBom(bomExcelFileFromState, mrpData);
      
      setProgress(100);
      setCurrentStep('Complete!');
      onBomProcessed(response.data);
      setSuccessMessage(response.data.message);
      antdMessage.success('BOM file and MRP data processed successfully!');
      setFileList([]);
      
      console.log('BOM processing completed successfully');
      console.log('Response:', response.data.message);
      
    } catch (err: any) {
      const apiError = err.response?.data?.detail || err.message || 'Failed to upload BOM file and MRP data.';
      setError(apiError);
      antdMessage.error(apiError);
      setProgress(0);
      setCurrentStep('');
      
      console.error('BOM processing failed:', apiError);
    }
    
    setLoading(false);
  };

  return (
    <div>
      <Upload {...props}>
        <Button icon={<UploadOutlined />} disabled={disabled}>
          Select BOM File (.xls, .xlsx)
        </Button>
      </Upload>
      
      {fileList.length > 0 && estimatedTime && !loading && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            <ClockCircleOutlined /> Estimated processing time: ~{formatTime(estimatedTime)}
          </Text>
        </div>
      )}
      
      <Button
        type="primary"
        onClick={handleUpload}
        disabled={fileList.length === 0 || disabled || loading}
        loading={loading}
        style={{ marginTop: 16 }}
      >
        {loading ? 'Processing...' : 'Process BOM File'}
      </Button>
      
      {loading && (
        <div style={{ marginTop: 16 }}>
          <Progress 
            percent={progress} 
            status={progress === 100 ? "success" : "active"}
            strokeColor={progress === 100 ? "#52c41a" : "#1890ff"}
          />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">{currentStep}</Text>
            <Text type="secondary">Elapsed: {formatTime(elapsedTime)}</Text>
          </div>
          {estimatedTime && elapsedTime < estimatedTime && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Estimated remaining: ~{formatTime(Math.max(0, estimatedTime - elapsedTime))}
            </Text>
          )}
          {elapsedTime > 60 && (
            <div style={{ marginTop: 4 }}>
              <Text type="warning" style={{ fontSize: '12px' }}>
                Large file detected - this may take several minutes to complete
              </Text>
            </div>
          )}
        </div>
      )}
      
      {error && (
        <Alert 
          message={error} 
          type="error" 
          showIcon 
          closable 
          style={{ marginTop: 16 }} 
          onClose={() => setError(null)} 
        />
      )}
      
      {successMessage && !error && (
        <Alert 
          message={successMessage} 
          type="success" 
          showIcon 
          style={{ marginTop: 16 }} 
        />
      )}
    </div>
  );
};

export default BomUpload;